
/* =========================================================
   HORIZON THEME - VARIANT IMAGE FILTER
   ---------------------------------------------------------
   Filters product gallery images based on the selected
   Color variant.

   Example:
   Image ALT:
      Red Front
      Red Side
      Red Back
      Blue Front
      Blue Side

   Selected Color:
      Red

   Result:
      Only Red images are displayed.
   ========================================================= */

(function () {
  'use strict';

  const CONFIG = {
    // Option names that should be treated as the color option
    colorOptionNames: ['color', 'colour'],

    // Class added to elements that should be hidden
    hiddenClass: 'variant-image-hidden',

    // Small delay helps Horizon finish its own variant update
    updateDelay: 50
  };


  /* =========================================================
     NORMALIZE TEXT
     ========================================================= */

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .trim();
  }


  /* =========================================================
     GET HORIZON VARIANT PICKER
     ========================================================= */

  function getVariantPicker() {
    return document.querySelector('variant-picker');
  }


  /* =========================================================
     GET SELECTED VARIANT OPTIONS
     ========================================================= */

  function getSelectedOptions(picker) {
    if (!picker) return [];

    const values = [];

    /*
      Horizon radio / swatch options
    */
    picker
      .querySelectorAll('input[type="radio"]:checked')
      .forEach((input) => {
        const optionName =
          input.getAttribute('data-option-name') || '';

        const optionValue = input.value || '';

        values.push({
          name: optionName,
          value: optionValue
        });
      });


    /*
      Horizon dropdown options
    */
    picker
      .querySelectorAll('select.variant-option__select')
      .forEach((select) => {
        const selectedOption = select.options[select.selectedIndex];

        if (!selectedOption) return;

        const optionName =
          select.getAttribute('data-option-name') ||
          selectedOption.getAttribute('data-option-name') ||
          '';

        const optionValue = selectedOption.value || '';

        values.push({
          name: optionName,
          value: optionValue
        });
      });


    return values;
  }


  /* =========================================================
     GET SELECTED COLOR
     ========================================================= */

  function getSelectedColor(picker) {
    const selectedOptions = getSelectedOptions(picker);

    if (!selectedOptions.length) {
      return null;
    }

    /*
      First try to find an option explicitly called
      Color / Colour.
    */
    const colorOption = selectedOptions.find((option) => {
      const name = normalize(option.name);

      return CONFIG.colorOptionNames.some((colorName) => {
        return name === normalize(colorName);
      });
    });

    if (colorOption && colorOption.value) {
      return colorOption.value;
    }

    /*
      If no Color option exists, don't filter.
      This prevents accidentally filtering a product
      based on Size / Material / Style etc.
    */
    return null;
  }


  /* =========================================================
     GET MEDIA GALLERY
     ========================================================= */

  function getMediaGallery() {
    return document.querySelector('media-gallery');
  }


  /* =========================================================
     GET IMAGE ALT
     ========================================================= */

  function getImageAlt(element) {
    if (!element) return '';

    /*
      Direct image
    */
    if (element.matches('img')) {
      return element.getAttribute('alt') || '';
    }

    /*
      Image inside the element
    */
    const image = element.querySelector('img');

    if (image) {
      return image.getAttribute('alt') || '';
    }

    return '';
  }


  /* =========================================================
     CHECK IF IMAGE MATCHES COLOR
     ========================================================= */

  function imageMatchesColor(element, selectedColor) {
    if (!selectedColor) return false;

    const imageAlt = normalize(getImageAlt(element));
    const color = normalize(selectedColor);

    if (!imageAlt || !color) {
      return false;
    }

    return imageAlt.includes(color);
  }


  /* =========================================================
     HIDE / SHOW ELEMENT
     ========================================================= */

  function setVisibility(element, visible) {
    if (!element) return;

    if (visible) {
      element.classList.remove(CONFIG.hiddenClass);
      element.removeAttribute('aria-hidden');
    } else {
      element.classList.add(CONFIG.hiddenClass);
      element.setAttribute('aria-hidden', 'true');
    }
  }


  /* =========================================================
     FILTER MAIN SLIDES
     ========================================================= */

  function filterSlides(gallery, selectedColor) {
    const slides = gallery.querySelectorAll(
      'slideshow-slide'
    );

    let matched = 0;

    slides.forEach((slide) => {
      const matches = imageMatchesColor(
        slide,
        selectedColor
      );

      setVisibility(slide, matches);

      if (matches) {
        matched++;
      }
    });

    return matched;
  }


  /* =========================================================
     FILTER PRODUCT GRID
     ========================================================= */

  function filterGrid(gallery, selectedColor) {
    const gridItems = gallery.querySelectorAll(
      '.media-gallery__grid > li'
    );

    gridItems.forEach((item) => {
      const matches = imageMatchesColor(
        item,
        selectedColor
      );

      setVisibility(item, matches);
    });
  }


  /* =========================================================
     FILTER MAIN MEDIA CONTAINERS
     ========================================================= */

  function filterMediaContainers(gallery, selectedColor) {
    const mediaItems = gallery.querySelectorAll(
      '.product-media-container'
    );

    mediaItems.forEach((item) => {
      /*
        Don't interfere with grid items here because the
        grid has its own filtering.
      */

      if (
        item.closest('.media-gallery__grid')
      ) {
        return;
      }

      const matches = imageMatchesColor(
        item,
        selectedColor
      );

      setVisibility(item, matches);
    });
  }


  /* =========================================================
     FILTER GALLERY THUMBNAILS
     ========================================================= */

  function filterThumbnails(gallery, selectedColor) {

    /*
      Horizon slideshow thumbnails
    */
    const thumbnails = gallery.querySelectorAll(
      '.slideshow-controls__thumbnail, ' +
      '.slideshow-controls button'
    );

    thumbnails.forEach((thumbnail) => {

      /*
        Don't blindly hide all controls because some
        controls may be arrows rather than thumbnails.
      */

      const image = thumbnail.querySelector('img');

      if (!image) return;

      const matches = imageMatchesColor(
        thumbnail,
        selectedColor
      );

      setVisibility(thumbnail, matches);
    });


    /*
      Horizon zoom dialog thumbnails
    */
    const zoomThumbnails = gallery.querySelectorAll(
      '.dialog-thumbnails-list__thumbnail'
    );

    zoomThumbnails.forEach((thumbnail) => {
      const matches = imageMatchesColor(
        thumbnail,
        selectedColor
      );

      setVisibility(thumbnail, matches);
    });
  }


  /* =========================================================
     FILTER ZOOM GALLERY
     ========================================================= */

  function filterZoomGallery(gallery, selectedColor) {

    const zoomItems = gallery.querySelectorAll(
      '.dialog-zoomed-gallery > *'
    );

    zoomItems.forEach((item) => {

      const matches = imageMatchesColor(
        item,
        selectedColor
      );

      setVisibility(item, matches);
    });
  }


  /* =========================================================
     SHOW EVERYTHING
     ========================================================= */

  function showAllGalleryItems(gallery) {
    if (!gallery) return;

    gallery
      .querySelectorAll(
        `.${CONFIG.hiddenClass}`
      )
      .forEach((element) => {
        element.classList.remove(
          CONFIG.hiddenClass
        );

        element.removeAttribute('aria-hidden');
      });
  }


  /* =========================================================
     UPDATE HORIZON SLIDESHOW
     ========================================================= */

  function refreshSlideshow(gallery) {

    /*
      Give Horizon a moment to finish its own
      variant/media update.
    */

    requestAnimationFrame(() => {

      const slideshows = gallery.querySelectorAll(
        'slideshow'
      );

      slideshows.forEach((slideshow) => {

        /*
          Horizon may expose different methods depending
          on the current theme version.
        */

        try {
          if (
            typeof slideshow.update === 'function'
          ) {
            slideshow.update();
          }
        } catch (error) {
          console.warn(
            'Variant image filter: slideshow update failed',
            error
          );
        }


        try {
          if (
            typeof slideshow.initPages === 'function'
          ) {
            slideshow.initPages();
          }
        } catch (error) {
          // Ignore
        }


        try {
          if (
            typeof slideshow.reset === 'function'
          ) {
            slideshow.reset();
          }
        } catch (error) {
          // Ignore
        }
      });


      /*
        Force layout recalculation.
      */
      window.dispatchEvent(
        new Event('resize')
      );
    });
  }


  /* =========================================================
     APPLY FILTER
     ========================================================= */

  function applyVariantImageFilter() {

    const picker = getVariantPicker();
    const gallery = getMediaGallery();

    if (!picker || !gallery) {
      return;
    }

    const selectedColor =
      getSelectedColor(picker);


    /*
      No color option / no selected color:
      show everything.
    */
    if (!selectedColor) {
      showAllGalleryItems(gallery);
      refreshSlideshow(gallery);
      return;
    }


    /*
      First hide everything.
    */
    showAllGalleryItems(gallery);


    /*
      Filter main slides.
    */
    const matchedSlides =
      filterSlides(
        gallery,
        selectedColor
      );


    /*
      Filter product grid.
    */
    filterGrid(
      gallery,
      selectedColor
    );


    /*
      Filter media containers.
    */
    filterMediaContainers(
      gallery,
      selectedColor
    );


    /*
      Filter thumbnails.
    */
    filterThumbnails(
      gallery,
      selectedColor
    );


    /*
      Filter zoom gallery.
    */
    filterZoomGallery(
      gallery,
      selectedColor
    );


    /*
      IMPORTANT:
      If no images matched the selected color,
      restore the complete gallery.
    */
    if (matchedSlides === 0) {
      showAllGalleryItems(gallery);
    }


    /*
      Let Horizon recalculate the slideshow.
    */
    refreshSlideshow(gallery);
  }


  /* =========================================================
     DEBOUNCED UPDATE
     ========================================================= */

  let updateTimer = null;

  function scheduleUpdate() {

    clearTimeout(updateTimer);

    updateTimer = setTimeout(() => {
      applyVariantImageFilter();
    }, CONFIG.updateDelay);
  }


  /* =========================================================
     LISTEN FOR VARIANT CHANGES
     ========================================================= */

  function setupVariantListeners() {

    const picker = getVariantPicker();

    if (!picker) return;


    /*
      Radio / swatch changes
    */
    picker.addEventListener(
      'change',
      scheduleUpdate
    );


    /*
      Listen to possible Horizon/custom theme events.
    */
    document.addEventListener(
      'variant:change',
      scheduleUpdate
    );

    document.addEventListener(
      'shopify:variant:change',
      scheduleUpdate
    );

    document.addEventListener(
      'product:variant-updated',
      scheduleUpdate
    );
  }


  /* =========================================================
     HANDLE DYNAMIC HORIZON DOM UPDATES
     ========================================================= */

  function setupMutationObserver() {

    const observer =
      new MutationObserver((mutations) => {

        let shouldUpdate = false;

        mutations.forEach((mutation) => {

          if (
            mutation.type === 'childList' ||
            mutation.type === 'attributes'
          ) {
            shouldUpdate = true;
          }
        });

        if (shouldUpdate) {
          scheduleUpdate();
        }
      });


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'checked',
          'selected',
          'data-current-checked'
        ]
      }
    );
  }


  /* =========================================================
     INITIALIZE
     ========================================================= */

  function init() {

    /*
      Wait until Horizon has rendered
      variant picker + media gallery.
    */
    setTimeout(() => {

      setupVariantListeners();

      setupMutationObserver();

      applyVariantImageFilter();

    }, 300);
  }


  /* =========================================================
     START
     ========================================================= */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  } else {

    init();

  }

})();


